import { characterConfig } from "@moe/priv/model/character";
import { z } from "zod";

const messageExampleSchema = z.object({
  role: z.string(),
  content: z.string()
});

export type MessageExample = z.infer<typeof messageExampleSchema>;

const baseSchema = z.object({
  title: z
    .string()
    .min(characterConfig.titleMinChars, `Title must be at least ${characterConfig.titleMinChars} character`)
    .max(characterConfig.titleMaxChars, `Title must be ${characterConfig.titleMaxChars} characters or less`),
  tagline: z
    .string()
    .min(characterConfig.taglineMinChars, `Tagline must be at least ${characterConfig.taglineMinChars} character`)
    .max(characterConfig.taglineMaxChars, `Tagline must be ${characterConfig.taglineMaxChars} characters or less`),
  name: z
    .string()
    .min(characterConfig.nameMinChars, `Name must be at least ${characterConfig.nameMinChars} character`)
    .max(characterConfig.nameMaxChars, `Name must be ${characterConfig.nameMaxChars} characters or less`),
  description: z
    .string()
    .max(
      characterConfig.descriptionMaxChars,
      `Description must be ${characterConfig.descriptionMaxChars} characters or less`
    ),
  scenario: z
    .string()
    .max(characterConfig.scenarioMaxChars, `Scenario must be ${characterConfig.scenarioMaxChars} characters or less`),
  greeting: z
    .string()
    .min(characterConfig.greetingMinChars, `Greeting must be at least ${characterConfig.greetingMinChars} character`)
    .max(characterConfig.greetingMaxChars, `Greeting must be ${characterConfig.greetingMaxChars} characters or less`),
  alternativeGreetings: z
    .array(
      z
        .string()
        .max(
          characterConfig.greetingMaxChars,
          `Greeting must be ${characterConfig.greetingMaxChars} characters or less`
        )
    )
    .max(
      characterConfig.alternativeGreetingsMaxCount,
      `You could only have a max of ${characterConfig.alternativeGreetingsMaxCount} alternative greetings`
    )
    .default([])
    .optional(),
  definitions: z
    .string()
    .max(
      characterConfig.definitionsMaxChars,
      `Definitions must be ${characterConfig.definitionsMaxChars} characters or less`
    ),
  messageExamples: z.array(messageExampleSchema).default([]),
  banner: z.instanceof(File).optional(),
  avatarDisplay: z.string().optional(),
  bannerDisplay: z.string().optional(),
  isNSFW: z.boolean().default(true),
  isPrivate: z.boolean().default(false)
});
export type BaseData = z.infer<typeof baseSchema>;

const baseDefaults: Partial<BaseData> = {
  title: "",
  tagline: "",
  banner: undefined,
  avatarDisplay: undefined,
  bannerDisplay: undefined,
  scenario: "",
  name: "",
  description: "",
  greeting: "",
  alternativeGreetings: [],
  definitions: "",
  messageExamples: [],
  isNSFW: true,
  isPrivate: false
};

export const createSchema = baseSchema.extend({
  avatar: z.instanceof(File)
});

export type CreateData = z.infer<typeof createSchema>;

export const createDefaults: Partial<CreateData> = {
  ...baseDefaults,
  avatar: undefined
};

export const updateSchema = baseSchema.extend({
  avatar: z.instanceof(File).optional()
});
export type UpdateData = z.infer<typeof updateSchema>;

export const updateDefaults: Partial<UpdateData> = {
  ...baseDefaults,
  avatar: undefined
};
